import { Nullable, RecursiveStatic } from '@diamond/shared/schema/lib/generics';
import { Static, Type } from '@sinclair/typebox';

export const RegistrationMasterItem = Type.Object({
  id: Type.String({ format: 'uuid' }),
  name: Type.String(),
  order: Type.Integer({ minimum: 0 }),
});

export const RegistrationServiceAreaItem = Type.Intersect([
  RegistrationMasterItem,
  Type.Object({ slug: Type.String({ maxLength: 50 }) }),
]);

export const RegistrationBusinessSegmentationItem = Type.Intersect([
  RegistrationMasterItem,
  Type.Object({
    code: Type.String({ maxLength: 50 }),
    desc: Nullable(Type.String()),
    icon: Nullable(Type.String()),
  }),
]);

export const RegistrationBusinessTypeItem = Type.Intersect([
  RegistrationMasterItem,
  Type.Object({
    code: Type.String({ maxLength: 50 }),
    icon: Nullable(Type.String()),
  }),
]);

export const RegistrationConsentTypeSchema = Type.Union([
  Type.Literal('data_processing'),
  Type.Literal('marketing'),
]);

export const RegistrationTncConsentItemSchema = Type.Object({
  type: RegistrationConsentTypeSchema,
  required: Type.Boolean(),
  label: Type.String(),
});

const RegistrationNeedRevisionSchema = Type.Object(
  {
    note: Nullable(Type.String()),
    fields: Type.Array(
      Type.Object(
        {
          field_name: Type.String({ minLength: 1 }),
          note: Type.String({ minLength: 1 }),
        },
        { additionalProperties: false }
      )
    ),
  },
  { additionalProperties: false }
);

const RegistrationNeedRevisionFieldSchema = Type.Union([
  RegistrationNeedRevisionSchema,
  Type.Null(),
]);

const RegistrationProgressUserStatus = Type.Union([
  Type.Literal('otp_verified'),
  Type.Literal('pending_review'),
  Type.Literal('in_review'),
  Type.Literal('need_revision'),
  Type.Literal('approved'),
]);

const RegistrationEntityStatus = Type.Union([
  Type.Literal('draft'),
  Type.Literal('pending_review'),
  Type.Literal('in_review'),
  Type.Literal('need_revision'),
  Type.Literal('approved'),
]);

export const RegistrationOutletAttachmentTypeSchema = Type.Union([
  Type.Literal('outlet_photo'),
  Type.Literal('pic_photo'),
  Type.Literal('kitchen_photo'),
  Type.Literal('menu_photo'),
]);

export const RegistrationBusinessDocumentTypeSchema = Type.Union([
  Type.Literal('company_npwp'),
  Type.Literal('pic_ktp'),
  Type.Literal('pic_selfie_ktp'),
  Type.Literal('owner_ktp'),
  Type.Literal('owner_selfie_ktp'),
]);

export const RegistrationBearerTokenSchema = Type.String({
  pattern:
    '^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\\.[0-9a-f]{64}$',
});

const RegistrationBusinessSectionSchema = Type.Object({
  status: Type.Optional(RegistrationEntityStatus),
  saved: Type.Boolean(),
  complete: Type.Boolean(),
  ownership_type: Type.Union([
    Type.Literal('company'),
    Type.Literal('individual'),
    Type.Null(),
  ]),
  missing_fields: Type.Array(Type.String()),
  documents_complete: Type.Boolean(),
  missing_documents: Type.Array(RegistrationBusinessDocumentTypeSchema),
});

const RegistrationProgressBusinessSectionSchema = Type.Intersect([
  RegistrationBusinessSectionSchema,
  Type.Object({
    need_revision: RegistrationNeedRevisionFieldSchema,
  }),
]);

const RegistrationProgressOutletItemSectionSchema = Type.Object({
  outlet_id: Type.String({ format: 'uuid' }),
  is_primary: Type.Boolean(),
  status: RegistrationEntityStatus,
  fields_complete: Type.Boolean(),
  attachments_complete: Type.Boolean(),
  complete: Type.Boolean(),
  missing_fields: Type.Array(Type.String()),
  missing_attachments: Type.Array(RegistrationOutletAttachmentTypeSchema),
  need_revision: RegistrationNeedRevisionFieldSchema,
});

const RegistrationProgressOutletsSectionSchema = Type.Object({
  complete: Type.Boolean(),
  count: Type.Integer({ minimum: 0 }),
  primary_count: Type.Integer({ minimum: 0 }),
  min_required: Type.Literal(1),
  max_allowed: Type.Literal(10),
  per_outlet: Type.Array(RegistrationProgressOutletItemSectionSchema),
});

const RegistrationProgressProductReferencesSectionSchema = Type.Object({
  saved: Type.Boolean(),
  complete: Type.Boolean(),
  count: Type.Integer({ minimum: 0 }),
  min_required: Type.Literal(1),
});

const RegistrationProgressSectionsSchema = Type.Object({
  business: RegistrationProgressBusinessSectionSchema,
  outlets: RegistrationProgressOutletsSectionSchema,
  product_references: RegistrationProgressProductReferencesSectionSchema,
});

const RegistrationOutletAttachmentResponseSchema = Type.Object({
  attachment_type: RegistrationOutletAttachmentTypeSchema,
  file_url: Nullable(Type.String()),
  original_filename: Nullable(Type.String()),
});

const RegistrationBusinessDocumentResponseSchema = Type.Object({
  document_type: RegistrationBusinessDocumentTypeSchema,
  file_url: Nullable(Type.String()),
  original_filename: Nullable(Type.String()),
});

const RegistrationBusinessAttachmentsResponseSchema = Type.Object({
  documents: Type.Array(RegistrationBusinessDocumentResponseSchema),
  sections: Type.Object({
    business: RegistrationBusinessSectionSchema,
  }),
});

const RegistrationKtpOcrDocumentTypeSchema = Type.Union([
  Type.Literal('pic_ktp'),
  Type.Literal('owner_ktp'),
]);

const NullableKtpString = Type.Union([Type.String(), Type.Null()]);

const RegistrationOcrLocationLevelSchema = Type.Union([
  Type.Object({
    id: Type.String({ format: 'uuid' }),
    name: Type.String(),
    display_name: Type.String(),
  }),
  Type.Null(),
]);

const RegistrationOcrLocationSchema = Type.Object({
  region: RegistrationOcrLocationLevelSchema,
  city: RegistrationOcrLocationLevelSchema,
  district: RegistrationOcrLocationLevelSchema,
  sub_district: RegistrationOcrLocationLevelSchema,
});

const RegistrationKtpOcrDataSchema = Type.Object({
  nik: NullableKtpString,
  nama_depan: NullableKtpString,
  nama_belakang: NullableKtpString,
  tempat_lahir: NullableKtpString,
  tanggal_lahir: NullableKtpString,
  jenis_kelamin: NullableKtpString,
  alamat: NullableKtpString,
  rt: NullableKtpString,
  rw: NullableKtpString,
  provinsi: NullableKtpString,
  kota: NullableKtpString,
  kecamatan: NullableKtpString,
  kelurahan: NullableKtpString,
  agama: NullableKtpString,
  status_perkawinan: NullableKtpString,
  pekerjaan: NullableKtpString,
  kewarganegaraan: NullableKtpString,
  confidence_score: Type.Union([
    Type.Number({ minimum: 0, maximum: 1 }),
    Type.Null(),
  ]),
  location: RegistrationOcrLocationSchema,
});

const RegistrationKtpOcrItemSchema = Type.Object({
  document_type: RegistrationKtpOcrDocumentTypeSchema,
  data: Type.Union([RegistrationKtpOcrDataSchema, Type.Null()]),
  error: Type.Union([
    Type.Literal('UNSUPPORTED_FILE_TYPE'),
    Type.Literal('NOT_KTP'),
    Type.Literal('OCR_FAILED'),
    Type.Null(),
  ]),
});

const RegistrationNpwpOcrDocumentTypeSchema = Type.Literal('company_npwp');

const NullableNpwpString = Type.Union([Type.String(), Type.Null()]);

const RegistrationNpwpOcrDataSchema = Type.Object({
  npwp_number: NullableNpwpString,
  nama_badan: NullableNpwpString,
  alamat: NullableNpwpString,
  provinsi: NullableNpwpString,
  kota: NullableNpwpString,
  kecamatan: NullableNpwpString,
  kelurahan: NullableNpwpString,
  confidence_score: Type.Union([
    Type.Number({ minimum: 0, maximum: 1 }),
    Type.Null(),
  ]),
  location: RegistrationOcrLocationSchema,
});

const RegistrationNpwpOcrItemSchema = Type.Object({
  document_type: RegistrationNpwpOcrDocumentTypeSchema,
  data: Type.Union([RegistrationNpwpOcrDataSchema, Type.Null()]),
  error: Type.Union([
    Type.Literal('UNSUPPORTED_FILE_TYPE'),
    Type.Literal('NOT_NPWP'),
    Type.Literal('OCR_FAILED'),
    Type.Null(),
  ]),
});

const RegistrationPutBusinessAttachmentsResponseSchema = Type.Intersect([
  RegistrationBusinessAttachmentsResponseSchema,
  Type.Object({
    ktp_ocr: Type.Array(RegistrationKtpOcrItemSchema),
    npwp_ocr: Type.Array(RegistrationNpwpOcrItemSchema),
  }),
]);

const RegistrationOutletResponseSchema = Type.Object({
  id: Type.String({ format: 'uuid' }),
  is_primary: Type.Boolean(),
  status: RegistrationEntityStatus,
  name: Nullable(Type.String()),
  social_media: Nullable(Type.String()),
  online_order_merchant_link: Nullable(Type.String()),
  phone: Nullable(Type.String()),
  email: Nullable(Type.String()),
  address: Nullable(Type.String()),
  latitude: Type.Union([Type.Number(), Type.Null()]),
  longitude: Type.Union([Type.Number(), Type.Null()]),
  cg_3_business_type: Nullable(Type.String()),
  use_primary_outlet_menu: Type.Boolean(),
  google_map_place_id: Nullable(Type.String()),
  google_map_types: Nullable(Type.Array(Type.String())),
  google_map_rating: Nullable(Type.Number()),
  google_map_user_ratings_total: Nullable(Type.Integer()),
  region_id: Nullable(Type.String({ format: 'uuid' })),
  city_id: Nullable(Type.String({ format: 'uuid' })),
  district_id: Nullable(Type.String({ format: 'uuid' })),
  sub_district_id: Nullable(Type.String({ format: 'uuid' })),
  postal_code_id: Nullable(Type.String({ format: 'uuid' })),
  region_label: Nullable(Type.String()),
  city_label: Nullable(Type.String()),
  district_label: Nullable(Type.String()),
  sub_district_label: Nullable(Type.String()),
  postal_code_label: Nullable(Type.String()),
  pic_role: Nullable(Type.String()),
  pic_role_other: Nullable(Type.String()),
  display_order: Type.Integer(),
  attachments: Type.Array(RegistrationOutletAttachmentResponseSchema),
});

const RegistrationBusinessProfileResponseSchema = Type.Object({
  ownership_type: Type.Union([
    Type.Literal('company'),
    Type.Literal('individual'),
  ]),
  status: RegistrationEntityStatus,
  npwp_number: Nullable(Type.String()),
  company_type: Nullable(Type.String()),
  company_name: Nullable(Type.String()),
  company_address: Nullable(Type.String()),
  region_id: Nullable(Type.String({ format: 'uuid' })),
  city_id: Nullable(Type.String({ format: 'uuid' })),
  district_id: Nullable(Type.String({ format: 'uuid' })),
  sub_district_id: Nullable(Type.String({ format: 'uuid' })),
  postal_code_id: Nullable(Type.String({ format: 'uuid' })),
  region_label: Nullable(Type.String()),
  city_label: Nullable(Type.String()),
  district_label: Nullable(Type.String()),
  sub_district_label: Nullable(Type.String()),
  postal_code_label: Nullable(Type.String()),
  pic_nik: Nullable(Type.String()),
  pic_first_name: Nullable(Type.String()),
  pic_last_name: Nullable(Type.String()),
  pic_address: Nullable(Type.String()),
  pic_rt: Nullable(Type.String()),
  pic_rw: Nullable(Type.String()),
  pic_phone: Nullable(Type.String()),
  pic_email: Nullable(Type.String()),
  owner_nik: Nullable(Type.String()),
  owner_first_name: Nullable(Type.String()),
  owner_last_name: Nullable(Type.String()),
  owner_address: Nullable(Type.String()),
  owner_rt: Nullable(Type.String()),
  owner_rw: Nullable(Type.String()),
  owner_phone: Nullable(Type.String()),
  owner_email: Nullable(Type.String()),
  company_npwp_url: Nullable(Type.String()),
  company_npwp_original_filename: Nullable(Type.String()),
  pic_ktp_url: Nullable(Type.String()),
  pic_ktp_original_filename: Nullable(Type.String()),
  pic_selfie_ktp_url: Nullable(Type.String()),
  pic_selfie_ktp_original_filename: Nullable(Type.String()),
  owner_ktp_url: Nullable(Type.String()),
  owner_ktp_original_filename: Nullable(Type.String()),
  owner_selfie_ktp_url: Nullable(Type.String()),
  owner_selfie_ktp_original_filename: Nullable(Type.String()),
  code_referral: Nullable(Type.String()),
});

const RegistrationProductReferenceResponseSchema = Type.Object({
  id: Type.String({ format: 'uuid' }),
  product_id: Type.String({ format: 'uuid' }),
  item_code: Type.Integer(),
  title: Type.String(),
});

const RegistrationCompanyType = Type.Union([
  Type.Literal('pt'),
  Type.Literal('cv'),
  Type.Literal('ud'),
  Type.Literal('koperasi'),
  Type.Literal('rumah_sakit'),
  Type.Literal('yayasan'),
  Type.Literal('others'),
]);

const RegistrationPutBusinessCompanyCoreFields = {
  ownership_type: Type.Literal('company'),
  npwp_number: Type.String({ minLength: 1, maxLength: 30 }),
  company_type: RegistrationCompanyType,
  company_name: Type.String({ minLength: 1, maxLength: 255 }),
  company_address: Type.String({ minLength: 1 }),
  region_id: Type.String({ format: 'uuid' }),
  city_id: Type.String({ format: 'uuid' }),
  district_id: Type.String({ format: 'uuid' }),
  sub_district_id: Type.String({ format: 'uuid' }),
  postal_code_id: Type.String({ format: 'uuid' }),
  code_referral: Type.Optional(Type.String({ maxLength: 100 })),
};

const RegistrationPutBusinessCompanyPicFields = {
  pic_nik: Type.String({ minLength: 16, maxLength: 16 }),
  pic_first_name: Type.String({ minLength: 1, maxLength: 100 }),
  pic_last_name: Type.String({ minLength: 1, maxLength: 100 }),
  pic_address: Type.String({ minLength: 1 }),
  pic_rt: Type.String({ minLength: 1, maxLength: 5 }),
  pic_rw: Type.String({ minLength: 1, maxLength: 5 }),
  pic_phone: Type.String({ minLength: 1, maxLength: 30 }),
  pic_email: Type.String({ format: 'email', maxLength: 255 }),
};

const RegistrationPutBusinessCompanyWithPicBodySchema = Type.Object(
  {
    ...RegistrationPutBusinessCompanyCoreFields,
    ...RegistrationPutBusinessCompanyPicFields,
  },
  { additionalProperties: false }
);

const RegistrationPutBusinessCompanyWithoutPicBodySchema = Type.Object(
  {
    ...RegistrationPutBusinessCompanyCoreFields,
  },
  { additionalProperties: false }
);

const RegistrationPutBusinessIndividualBodySchema = Type.Object(
  {
    ownership_type: Type.Literal('individual'),
    owner_nik: Type.String({ minLength: 16, maxLength: 16 }),
    owner_first_name: Type.String({ minLength: 1, maxLength: 100 }),
    owner_last_name: Type.String({ minLength: 1, maxLength: 100 }),
    owner_address: Type.String({ minLength: 1 }),
    owner_rt: Type.String({ minLength: 1, maxLength: 5 }),
    owner_rw: Type.String({ minLength: 1, maxLength: 5 }),
    owner_phone: Type.String({ minLength: 1, maxLength: 30 }),
    owner_email: Type.String({ format: 'email', maxLength: 255 }),
    region_id: Type.String({ format: 'uuid' }),
    city_id: Type.String({ format: 'uuid' }),
    district_id: Type.String({ format: 'uuid' }),
    sub_district_id: Type.String({ format: 'uuid' }),
    postal_code_id: Type.String({ format: 'uuid' }),
    code_referral: Type.Optional(Type.String({ maxLength: 100 })),
  },
  { additionalProperties: false }
);

const RegistrationPostOutletBodySchema = Type.Object({
  name: Type.String({ minLength: 1, maxLength: 255 }),
  social_media: Type.Optional(Type.String()),
  online_order_merchant_link: Type.Optional(Type.String()),
  phone: Type.String({ minLength: 1 }),
  email: Type.String({ format: 'email', maxLength: 255 }),
  address: Type.String({ minLength: 1 }),
  region_id: Type.Optional(Nullable(Type.String({ format: 'uuid' }))),
  city_id: Type.Optional(Nullable(Type.String({ format: 'uuid' }))),
  district_id: Type.Optional(Nullable(Type.String({ format: 'uuid' }))),
  sub_district_id: Type.Optional(Nullable(Type.String({ format: 'uuid' }))),
  postal_code_id: Type.Optional(Nullable(Type.String({ format: 'uuid' }))),
  latitude: Type.Number({ minimum: -90, maximum: 90 }),
  longitude: Type.Number({ minimum: -180, maximum: 180 }),
  cg_3_business_type: Type.String({ minLength: 1 }),
  is_primary: Type.Optional(Type.Boolean()),
  use_primary_outlet_menu: Type.Optional(Type.Boolean()),
  code_referral: Type.Optional(Type.String({ maxLength: 100 })),
  google_map_place_id: Type.Optional(Nullable(Type.String())),
  google_map_types: Type.Optional(Nullable(Type.Array(Type.String()))),
  google_map_rating: Type.Optional(
    Nullable(Type.Number({ minimum: 1, maximum: 5 }))
  ),
  google_map_user_ratings_total: Type.Optional(
    Nullable(Type.Integer({ minimum: 0, maximum: 2147483647 }))
  ),
  pic_role: Type.Optional(Nullable(Type.String())),
  pic_role_other: Type.Optional(Nullable(Type.String())),
});

export const RegistrationCrudSchema = {
  path: '/registration',
  getServiceAreas: {
    path: '/service-areas',
    response: Type.Object({
      data: Type.Array(RegistrationServiceAreaItem),
    }),
  },
  getBusinessSegmentations: {
    path: '/business-segmentations',
    response: Type.Object({
      data: Type.Array(RegistrationBusinessSegmentationItem),
    }),
  },
  getBusinessTypes: {
    path: '/business-types',
    response: Type.Object({
      data: Type.Array(RegistrationBusinessTypeItem),
    }),
  },
  getTnc: {
    path: '/tnc',
    response: Type.Object({
      id: Type.String({ format: 'uuid' }),
      version: Type.Integer(),
      title: Type.String(),
      body: Type.String(),
      consents: Type.Array(RegistrationTncConsentItemSchema, {
        minItems: 2,
        maxItems: 2,
      }),
    }),
  },
  checkEmail: {
    path: '/check-email',
    body: Type.Object({
      email: Type.String({ format: 'email', maxLength: 255 }),
    }),
    response: Type.Object({
      available: Type.Literal(true),
    }),
  },
  createRegistration: {
    path: '/',
    body: Type.Object({
      service_area_id: Type.String({ format: 'uuid' }),
      business_segmentation_id: Type.String({ format: 'uuid' }),
      email: Type.String({ format: 'email', maxLength: 255 }),
      phone_number: Type.String({ maxLength: 30 }),
      password: Type.String({ minLength: 5 }),
      confirm_password: Type.String({ minLength: 5 }),
    }),
    response: Type.Object({
      registration_id: Type.String({ format: 'uuid' }),
      status: Type.Literal('otp_pending'),
      message: Type.String(),
      otp_expires_in_seconds: Type.Integer({ minimum: 1 }),
    }),
  },
  verifyOtp: {
    path: '/verify-otp',
    body: Type.Object({
      registration_id: Type.String({ format: 'uuid' }),
      otp: Type.String({ pattern: '^\\d{6}$' }),
    }),
    response: Type.Object({
      registration_id: Type.String({ format: 'uuid' }),
      status: Type.Literal('otp_verified'),
      registration_token: RegistrationBearerTokenSchema,
      message: Type.String(),
    }),
  },
  resendOtp: {
    path: '/resend-otp',
    body: Type.Object({
      registration_id: Type.String({ format: 'uuid' }),
    }),
    response: Type.Object({
      registration_id: Type.String({ format: 'uuid' }),
      message: Type.String(),
      otp_expires_in_seconds: Type.Integer({ minimum: 1 }),
    }),
  },
  getProgress: {
    path: '/progress',
    response: Type.Object({
      registration_id: Type.String({ format: 'uuid' }),
      registration_status: RegistrationProgressUserStatus,
      registration_submitted_at: Type.Union([
        Type.Integer({ minimum: 0 }), // epoch milliseconds
        Type.Null(),
      ]),
      sections: RegistrationProgressSectionsSchema,
      business: Type.Union([
        RegistrationBusinessProfileResponseSchema,
        Type.Null(),
      ]),
      outlets: Type.Array(RegistrationOutletResponseSchema),
      product_references: Type.Array(
        RegistrationProductReferenceResponseSchema
      ),
    }),
  },
  putBusiness: {
    path: '/business',
    body: Type.Union([
      RegistrationPutBusinessCompanyWithPicBodySchema,
      RegistrationPutBusinessCompanyWithoutPicBodySchema,
      RegistrationPutBusinessIndividualBodySchema,
    ]),
    response: Type.Object({
      business: RegistrationBusinessProfileResponseSchema,
    }),
  },
  putBusinessAttachments: {
    path: '/business/attachments',
    response: RegistrationPutBusinessAttachmentsResponseSchema,
  },
  deleteBusinessAttachment: {
    path: '/business/attachments/:document_type',
    params: Type.Object({
      document_type: Type.String(),
    }),
    querystring: Type.Object({
      ownership_type: Type.Union([
        Type.Literal('company'),
        Type.Literal('individual'),
      ]),
    }),
    response: RegistrationBusinessAttachmentsResponseSchema,
  },
  getOutlets: {
    path: '/outlets',
    response: Type.Object({
      data: Type.Array(RegistrationOutletResponseSchema),
    }),
  },
  postOutlets: {
    path: '/outlets',
    body: RegistrationPostOutletBodySchema,
    response: Type.Object({
      outlet: RegistrationOutletResponseSchema,
    }),
  },
  putOutlet: {
    path: '/outlets/:outlet_id',
    params: Type.Object({
      outlet_id: Type.String({ format: 'uuid' }),
    }),
    body: RegistrationPostOutletBodySchema,
    response: Type.Object({
      outlet: RegistrationOutletResponseSchema,
    }),
  },
  deleteOutlet: {
    path: '/outlets/:outlet_id',
    params: Type.Object({
      outlet_id: Type.String({ format: 'uuid' }),
    }),
    response: Type.Object({
      message: Type.Literal('Outlet berhasil dihapus'),
    }),
  },
  putOutletAttachments: {
    path: '/outlets/:outlet_id/attachments',
    params: Type.Object({
      outlet_id: Type.String({ format: 'uuid' }),
    }),
    response: Type.Object({
      attachments: Type.Array(RegistrationOutletAttachmentResponseSchema),
    }),
  },
  deleteOutletAttachment: {
    path: '/outlets/:outlet_id/attachments/:attachment_type',
    params: Type.Object({
      outlet_id: Type.String({ format: 'uuid' }),
      attachment_type: Type.String(),
    }),
    response: Type.Object({
      attachments: Type.Array(RegistrationOutletAttachmentResponseSchema),
    }),
  },
  putProductReferences: {
    path: '/product-references',
    body: Type.Object({
      item_codes: Type.Array(Type.Integer({ minimum: 1 }), { minItems: 1 }),
    }),
    response: Type.Object({
      product_references: Type.Array(
        RegistrationProductReferenceResponseSchema
      ),
      sections: Type.Object({
        product_references: RegistrationProgressProductReferencesSectionSchema,
      }),
    }),
  },
  postSubmit: {
    path: '/submit',
    body: Type.Object(
      {
        tnc_id: Type.String({ format: 'uuid' }),
        consents: Type.Object({
          data_processing: Type.Literal(true),
          marketing: Type.Boolean(),
        }),
      },
      { additionalProperties: false }
    ),
    response: Type.Union([
      Type.Object({
        registration_id: Type.String({ format: 'uuid' }),
        registration_status: Type.Literal('pending_review'),
        registration_submitted_at: Type.Integer({ minimum: 0 }), // epoch milliseconds
        message: Type.Literal(
          'Registrasi berhasil dikirim dan sedang ditinjau'
        ),
      }),
      Type.Object({
        registration_id: Type.String({ format: 'uuid' }),
        registration_status: Type.Literal('in_review'),
        registration_submitted_at: Type.Integer({ minimum: 0 }), // epoch milliseconds
        ticket_number_sol: Type.String({ minLength: 1 }),
        message: Type.Literal(
          'Registrasi berhasil dikirim dan sedang ditinjau'
        ),
      }),
    ]),
  },
};

export const RegistrationResumeRegistrationSchema = Type.Object({
  registration_id: Type.String({ format: 'uuid' }),
  status: Type.Union([
    Type.Literal('otp_pending'),
    Type.Literal('otp_verified'),
    Type.Literal('pending_review'),
    Type.Literal('in_review'),
    Type.Literal('need_revision'),
    Type.Literal('approved'),
    Type.Literal('cancelled'),
  ]),
});

export const RegistrationResumeDataSchema = Type.Object({
  code: Type.Literal('REGISTRATION_INCOMPLETE'),
  registration: RegistrationResumeRegistrationSchema,
  // Present when registration.status is in REGISTRATION_READ_ALLOWED_STATUSES (login resume).
  registration_token: Type.Optional(RegistrationBearerTokenSchema),
});

export type RegistrationResumeData = Static<
  typeof RegistrationResumeDataSchema
>;

export const RegistrationPasswordUpdatedDataSchema = Type.Object({
  code: Type.Literal('REGISTRATION_PASSWORD_UPDATED'),
  registration: RegistrationResumeRegistrationSchema,
  registration_token: Type.Optional(RegistrationBearerTokenSchema),
});

export type RegistrationPasswordUpdatedData = Static<
  typeof RegistrationPasswordUpdatedDataSchema
>;

export type RegistrationCrud = RecursiveStatic<typeof RegistrationCrudSchema>;
